import {
  disableEditAtom,
  isAddingAtom,
  listDataAtom,
  mutationAddKeyAtom,
  mutationDeleteKeyAtom,
  mutationUpdateKeyAtom,
  updateMapAtom,
  updatingIdAtom,
} from '../atoms'
import { Box, Button } from '@mui/material'
import { ConfirmCommitButton } from 'shared/ui'
import { NoPropagationButton } from '../../../utility/NoPropagationButton'
import React, { useCallback } from 'react'
import {
  useAtomValue,
  useAtom,
  useSetAtom,
} from 'infrastructure/state/jotai.wrapper'
import { doAdminQueriesWithResult, IQueryInfo } from 'infrastructure/api'
import { NEW_ITEM_ID } from '../types'
import { useWithErrorNotification } from 'shared/ui/GlobalDialog'

export function UpdateColumn({ id }: { id: number }) {
  const [isAdding, setAdding] = useAtom(isAddingAtom)
  const disableEdit = useAtomValue(disableEditAtom)
  const doUpdate = useDoUpdate()
  const doAdd = useDoAdd()
  const doDelete = useDoDelete()
  const setEditMap = useSetAtom(updateMapAtom)
  const [currentEditId, setCurrentEditId] = useAtom(updatingIdAtom)
  if (currentEditId === id) {
    return (
      <Box display="flex" flexDirection="column">
        <ConfirmCommitButton onClick={doUpdate}>Do Update</ConfirmCommitButton>
        <Button
          onClick={() => {
            setEditMap({})
            setCurrentEditId(0)
          }}
        >
          Cancel
        </Button>
      </Box>
    )
  }

  if (id === NEW_ITEM_ID) {
    if (!isAdding) return null
    return (
      <Box>
        <ConfirmCommitButton onClick={doAdd}>Do Add</ConfirmCommitButton>
        <Button
          disabled={!!currentEditId}
          onClick={() => {
            setAdding(false)
          }}
        >
          Cancel
        </Button>
      </Box>
    )
  }

  return (
    <Box display="flex" flexDirection="column">
      <NoPropagationButton
        disabled={!!currentEditId || isAdding || disableEdit}
        onClick={() => {
          setCurrentEditId(id)
          setEditMap({})
        }}
      >
        Update
      </NoPropagationButton>
      <NoPropagationButton doConfirm={true} onClick={() => doDelete(id)}>
        Delete
      </NoPropagationButton>
    </Box>
  )
}

function useDoUpdate() {
  const [currentEditId, setCurrentEditId] = useAtom(updatingIdAtom)
  const [editMap] = useAtom(updateMapAtom)
  const reFetch = useSetAtom(listDataAtom)
  const mutationKey = useAtomValue(mutationUpdateKeyAtom)
  const doUpdate = useWithErrorNotification(
    { errorTitle: `Error Updating: ${mutationKey}` },
    doAdminQueriesWithResult,
  )

  return useCallback(async () => {
    if (!currentEditId || !mutationKey || !Object.values(editMap).length) return
    const query: IQueryInfo = {
      resourceData: { key: mutationKey },
      params: { id: currentEditId, ...editMap },
    }
    await doUpdate([query])
    setCurrentEditId(0)
    return reFetch()
  }, [currentEditId, editMap, mutationKey, reFetch, setCurrentEditId])
}

function useDoAdd() {
  const setIsAdding = useSetAtom(isAddingAtom)
  const [editMap] = useAtom(updateMapAtom)
  const reFetch = useSetAtom(listDataAtom)
  const mutationKey = useAtomValue(mutationAddKeyAtom)
  const doAdd = useWithErrorNotification(
    { errorTitle: 'Error Adding' },
    doAdminQueriesWithResult,
  )

  return useCallback(async () => {
    if (!mutationKey || !Object.values(editMap).length) return
    const query: IQueryInfo = {
      resourceData: { key: mutationKey },
      params: { ...editMap },
    }
    const result = await doAdd([query])
    if (result) {
      reFetch()
      setIsAdding(false)
    }
  }, [editMap, mutationKey, reFetch, setIsAdding])
}

function useDoDelete() {
  const reFetch = useSetAtom(listDataAtom)
  const mutationKey = useAtomValue(mutationDeleteKeyAtom)
  const doDelete = useWithErrorNotification(
    { errorTitle: `Error Deleting: ${mutationKey}` },
    doAdminQueriesWithResult,
  )
  return useCallback(
    async (deleteId: string | number) => {
      if (!mutationKey) return
      const query: IQueryInfo = {
        resourceData: { key: mutationKey },
        params: { id: deleteId },
      }
      await doDelete([query])
      return reFetch()
    },
    [mutationKey, reFetch],
  )
}
